import React from 'react';
import useOffensiveStore from '../../stores/offensiveStore';
import './AttackChainKillChain.css';

const PHASE_META = {
  TA0043: { short: 'RECON', label: 'Reconnaissance' },
  TA0042: { short: 'RSRC', label: 'Resource Dev' },
  TA0001: { short: 'ACCESS', label: 'Initial Access' },
  TA0002: { short: 'EXEC', label: 'Execution' },
  TA0003: { short: 'PERSIST', label: 'Persistence' },
  TA0004: { short: 'PRIVESC', label: 'Privilege Esc' },
  TA0005: { short: 'EVADE', label: 'Defense Evasion' },
  TA0006: { short: 'CREDS', label: 'Credential Access' },
  TA0007: { short: 'DISC', label: 'Discovery' },
  TA0008: { short: 'LATERAL', label: 'Lateral Movement' },
  TA0009: { short: 'COLLECT', label: 'Collection' },
  TA0011: { short: 'C2', label: 'Command & Control' },
  TA0010: { short: 'EXFIL', label: 'Exfiltration' },
  TA0040: { short: 'IMPACT', label: 'Impact' },
};

const PHASE_COLORS = {
  TA0043: { accent: '#60a5fa', glow: 'rgba(96, 165, 250, 0.25)' },
  TA0042: { accent: '#ec4899', glow: 'rgba(236, 72, 153, 0.25)' },
  TA0001: { accent: '#8b5cf6', glow: 'rgba(139, 92, 246, 0.25)' },
  TA0002: { accent: '#f59e0b', glow: 'rgba(245, 158, 11, 0.25)' },
  TA0003: { accent: '#ef4444', glow: 'rgba(239, 68, 68, 0.25)' },
  TA0004: { accent: '#10b981', glow: 'rgba(16, 185, 129, 0.25)' },
  TA0005: { accent: '#06b6d4', glow: 'rgba(6, 182, 212, 0.25)' },
  TA0006: { accent: '#f472b6', glow: 'rgba(244, 114, 182, 0.25)' },
  TA0007: { accent: '#3b82f6', glow: 'rgba(59, 130, 246, 0.25)' },
  TA0008: { accent: '#a78bfa', glow: 'rgba(167, 139, 250, 0.25)' },
  TA0009: { accent: '#6366f1', glow: 'rgba(99, 102, 241, 0.25)' },
  TA0011: { accent: '#84cc16', glow: 'rgba(132, 204, 22, 0.25)' },
  TA0010: { accent: '#fb923c', glow: 'rgba(251, 146, 60, 0.25)' },
  TA0040: { accent: '#f43f5e', glow: 'rgba(244, 63, 94, 0.25)' },
};

const TACTIC_ICONS = {
  TA0043: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="22" cy="36" r="9"/><circle cx="42" cy="36" r="9"/>
      <path d="M31 36 H33"/><path d="M16 28 L22 22 M48 28 L42 22"/>
      <path d="M22 22 H42"/>
    </g>
  ),
  TA0042: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M32 14 L50 24 V44 L32 54 L14 44 V24 Z"/>
      <path d="M14 24 L32 34 L50 24"/><path d="M32 34 V54"/>
    </g>
  ),
  TA0001: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="20" y="12" width="24" height="40" rx="2"/>
      <circle cx="38" cy="32" r="2"/><path d="M10 32 H20"/>
      <path d="M16 28 L20 32 L16 36"/>
    </g>
  ),
  TA0002: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="12" y="16" width="40" height="32" rx="3"/>
      <path d="M20 28 L28 34 L20 40"/><path d="M32 40 H42"/>
    </g>
  ),
  TA0003: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="32" cy="18" r="6"/><path d="M32 24 V50"/>
      <path d="M18 38 Q18 50 32 50 Q46 50 46 38"/>
      <path d="M26 32 H38"/>
    </g>
  ),
  TA0004: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M32 10 L48 20 V36 Q48 50 32 54 Q16 50 16 36 V20 Z"/>
      <path d="M32 40 V24"/><path d="M26 30 L32 24 L38 30"/>
    </g>
  ),
  TA0005: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 32 Q20 18 32 18 Q44 18 56 32 Q44 46 32 46 Q20 46 8 32 Z"/>
      <circle cx="32" cy="32" r="7"/><path d="M14 50 L50 14"/>
    </g>
  ),
  TA0006: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="22" cy="26" r="10"/><path d="M30 32 L50 52"/>
      <path d="M42 44 L48 44"/><path d="M38 40 L44 40"/>
    </g>
  ),
  TA0007: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="32" cy="32" r="20"/><circle cx="32" cy="32" r="12"/>
      <circle cx="32" cy="32" r="4"/><path d="M32 32 L44 20"/>
    </g>
  ),
  TA0008: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="16" cy="32" r="6"/><circle cx="48" cy="32" r="6"/>
      <circle cx="32" cy="16" r="6"/><path d="M22 32 H42"/>
      <path d="M20 27 L28 21"/><path d="M44 27 L36 21"/>
    </g>
  ),
  TA0009: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 20 H26 L30 16 H50 V48 H14 Z"/>
      <path d="M32 28 V42"/><path d="M26 36 L32 42 L38 36"/>
    </g>
  ),
  TA0011: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M32 20 V52"/><path d="M24 52 H40"/>
      <path d="M22 28 Q22 14 32 14 Q42 14 42 28"/>
      <path d="M16 34 Q16 10 32 10 Q48 10 48 34"/>
      <circle cx="32" cy="20" r="3"/>
    </g>
  ),
  TA0010: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 40 Q10 40 10 32 Q10 24 18 24 Q20 16 30 16 Q40 16 42 24 Q50 24 50 32 Q50 40 42 40"/>
      <path d="M32 30 V48"/><path d="M26 36 L32 30 L38 36"/>
    </g>
  ),
  TA0040: (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M32 10 L36 26 L50 22 L40 32 L54 38 L38 38 L42 54 L32 42 L22 54 L26 38 L10 38 L24 32 L14 22 L28 26 Z"/>
    </g>
  ),
};

function StatusDot({ status }) {
  if (status === 'completed') {
    return (
      <div className="status-dot status-completed">
        <svg viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" stroke="#22c55e" strokeWidth="1.5" fill="rgba(34,197,94,0.12)" />
          <path d="M6.5 10 L9 12.5 L13.5 7.5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    );
  }
  if (status === 'active') {
    return (
      <div className="status-dot status-active">
        <svg viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="4" fill="var(--accent)" />
          <circle cx="10" cy="10" r="7" stroke="var(--accent)" strokeWidth="1.5" opacity="0.4" />
        </svg>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="status-dot status-failed">
        <svg viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" stroke="#ef4444" strokeWidth="1.5" fill="rgba(239,68,68,0.12)" />
          <path d="M7.5 7.5 L12.5 12.5 M12.5 7.5 L7.5 12.5" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
    );
  }
  return (
    <div className="status-dot status-pending">
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="7" stroke="rgba(148,163,184,0.25)" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

function CardConnector({ prevStatus, color }) {
  const isActive = prevStatus === 'active';
  const isDone = prevStatus === 'completed';
  const isFailed = prevStatus === 'failed';
  const lineColor = isDone || isFailed ? color : isActive ? color : 'rgba(148,163,184,0.1)';
  const nodeStroke = isDone || isActive ? color : 'rgba(148,163,184,0.18)';
  const nodeFill = isDone || isActive ? color : 'transparent';

  return (
    <div className="card-connector">
      <svg width="48" height="32" viewBox="0 0 48 32" className="connector-svg">
        <line
          x1="0" y1="16" x2="18" y2="16"
          stroke={lineColor}
          strokeWidth={isDone ? 2 : 1.5}
          className={`connector-line ${isActive ? 'active' : ''}`}
        />
        <line
          x1="30" y1="16" x2="48" y2="16"
          stroke={lineColor}
          strokeWidth={isDone ? 2 : 1.5}
          className={`connector-line ${isActive ? 'active' : ''}`}
        />
        <circle
          cx="24" cy="16" r="5"
          fill="transparent"
          stroke={nodeStroke}
          strokeWidth="1.5"
          className={`connector-node ${isActive ? 'active' : ''}`}
        />
        <circle
          cx="24" cy="16" r="2"
          fill={nodeFill}
          opacity={isDone ? 0.8 : isActive ? 0.6 : 0}
        />
      </svg>
    </div>
  );
}

const CARDS_PER_ROW = 5;

function RowTurnConnector({ color, status }) {
  const isActive = status === 'active';
  const isDone = status === 'completed' || status === 'failed';
  const lineColor = isDone || isActive ? color : 'rgba(148,163,184,0.1)';
  const nodeColor = isDone || isActive ? color : 'rgba(148,163,184,0.18)';
  const nodeFill = isDone || isActive ? nodeColor : 'transparent';
  const pathClass = `turn-curve ${isDone ? 'completed' : isActive ? 'active' : ''}`;

  return (
    <div className="row-turn-full">
      <svg className="turn-curve-svg" viewBox="0 0 1000 100" preserveAspectRatio="none">
        <path
          d="M 918 0 L 918 25 Q 918 50 893 50 L 107 50 Q 82 50 82 75 L 82 100"
          fill="none" stroke={lineColor}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
          className={pathClass}
        />
      </svg>
      <svg className="turn-node-right" viewBox="0 0 20 20">
        <circle cx="10" cy="10" r="5" stroke={nodeColor} fill="transparent" strokeWidth="1.5"
                className={isActive ? 'turn-node-active' : ''} />
        <circle cx="10" cy="10" r="2" fill={nodeFill} opacity="0.7" />
      </svg>
      <svg className="turn-node-left" viewBox="0 0 20 20">
        <circle cx="10" cy="10" r="5" stroke={nodeColor} fill="transparent" strokeWidth="1.5"
                className={isActive ? 'turn-node-active' : ''} />
        <circle cx="10" cy="10" r="2" fill={nodeFill} opacity="0.7" />
      </svg>
    </div>
  );
}

const AttackChainKillChain = ({ collapsed = false, onToggleCollapse }) => {
  const store = useOffensiveStore();
  const tactics = store.tactics;
  const activeTactic = store.activeTactic;
  const gridRef = React.useRef(null);

  React.useEffect(() => {
    if (activeTactic && gridRef.current) {
      const activeIndex = tactics.findIndex(t => t.id === activeTactic);
      if (activeIndex !== -1) {
        const cards = gridRef.current.querySelectorAll('.phase-card');
        const el = cards[activeIndex];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }
  }, [activeTactic, tactics]);

  const visibleTactics = tactics.filter(t => t.status !== 'pending' || PHASE_META[t.id]);
  const activeCount = visibleTactics.filter(t => t.status === 'active' || t.status === 'completed').length;
  const activeLabel = visibleTactics.find(t => t.id === activeTactic);

  const rows = [];
  for (let i = 0; i < visibleTactics.length; i += CARDS_PER_ROW) {
    rows.push(visibleTactics.slice(i, i + CARDS_PER_ROW));
  }

  return (
    <div className={`attack-chain-killchain ${collapsed ? 'collapsed' : ''}`}>
      {onToggleCollapse && (
        <button
          className="chain-toggle"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand kill chain' : 'Collapse kill chain'}
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="chain-toggle-icon">
            {collapsed
              ? <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              : <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            }
          </svg>
          {collapsed && (
            <span className="chain-toggle-summary">
              Kill Chain — {activeCount}/{visibleTactics.length} phases
              {activeLabel ? ` · ${PHASE_META[activeLabel.id]?.label || activeLabel.name || ''}` : ''}
            </span>
          )}
        </button>
      )}

      {!collapsed && (
        <div className="chain-container">
          <div className="chain-grid" ref={gridRef}>
            {rows.map((row, rowIndex) => {
              const lastTactic = row[row.length - 1];
              const lastColors = PHASE_COLORS[lastTactic.id] || { accent: '#818cf8', glow: 'rgba(129, 140, 248, 0.25)' };

              return (
                <React.Fragment key={rowIndex}>
                  <div className="chain-row">
                    {row.map((tactic, colIndex) => {
                      const meta = PHASE_META[tactic.id] || {
                        short: tactic.shortName || `T${rowIndex * CARDS_PER_ROW + colIndex + 1}`,
                        label: tactic.name || tactic.id,
                      };
                      const colors = PHASE_COLORS[tactic.id] || { accent: '#818cf8', glow: 'rgba(129, 140, 248, 0.25)' };
                      const isActive = activeTactic === tactic.id;
                      const progress = tactic.status === 'completed' ? 100
                        : tactic.status === 'active' ? (tactic.progress || 30)
                        : tactic.status === 'failed' ? 100
                        : 0;

                      const prevInRow = colIndex > 0 ? row[colIndex - 1] : null;
                      const prevColors = prevInRow ? (PHASE_COLORS[prevInRow.id] || colors) : null;

                      return (
                        <React.Fragment key={tactic.id}>
                          {colIndex > 0 && prevInRow && (
                            <CardConnector
                              prevStatus={prevInRow.status}
                              color={prevColors.accent}
                            />
                          )}

                          <div
                            className={`phase-card ${tactic.status} ${isActive ? 'focused' : ''}`}
                            style={{ '--accent': colors.accent, '--glow': colors.glow }}
                          >
                            <div className="card-content-row">
                              <div className="card-icon-box">
                                <svg viewBox="0 0 64 64" className="card-icon">
                                  {TACTIC_ICONS[tactic.id] || <circle cx="32" cy="32" r="12" fill="none" stroke="currentColor" strokeWidth="4"/>}
                                </svg>
                              </div>
                              <div className="card-text">
                                <span className="card-code-badge">{meta.short}</span>
                                <span className="card-phase-name">{meta.label}</span>
                              </div>
                              <StatusDot status={tactic.status} />
                            </div>

                            <div className="card-progress-track">
                              <div
                                className="card-progress-fill"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {rowIndex < rows.length - 1 && (
                    <RowTurnConnector
                      color={lastColors.accent}
                      status={lastTactic.status}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttackChainKillChain;
